import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  RESTO365_RSS,
  RESTO365_RSS_INITIAL_STATE,
  RESTO365_RSS_MASTER_TASKS,
  RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS,
  RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS_BULK_UPDATE,
  RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS_BULK_UPDATE_STATUS,
  RESTO365_RSS_RESTAURANT_TASKS_BY_RESTAURANT_ID,
  RESTO365_RSS_RESTAURANT_TASKS_CREATE,
  RESTO365_RSS_RESTAURANT_TASKS_DELETE,
  RESTO365_RSS_RESTAURANT_TASKS_RESTORE,
  RESTO365_RSS_RESTAURANT_TASKS_SCHEDULE_CREATE,
  RESTO365_RSS_RESTAURANT_TASKS_SCHEDULE_UPDATE,
  RESTO365_RSS_RESTAURANT_TASKS_SUBMISSIONS_BY_ID,
  RESTO365_RSS_RESTAURANT_TASKS_SUBMISSIONS_BY_RESTAURANT_ID,
  RESTO365_RSS_RESTAURANT_TASKS_UPDATE
} from './action'
import {
  MasterTask,
  RestaurantTask,
  RestaurantScheduledTask,
  RestaurantTaskSchedule
} from '@/src/types/apps/resto365Rss'
import { DayParts, Frequencies } from '@/src/types/apps/gygRssTypes'

interface RssInitialState {
  rss: MasterTask
  masterTasksSuccess: MasterTask[]
  restaurantTasksSuccess: RestaurantTask[]
  restaurantTasksScheduleSuccess: RestaurantTaskSchedule[]
  restaurantScheduledTasksSuccess: RestaurantScheduledTask[]
  restaurantScheduledTasksBulkUpdateSuccess: RestaurantScheduledTask[]
  loading: boolean
  success: string | null
  errors: string | null
  reportingSuccess: string | null
  reportingError: string | null
  imageUpload: any
  imageUploadSuccess: string | null
  imageUploadError: string | null
  formSuccess: string | null
  formError: string | null
  allRssData: any[]
  selectedSubmissionDetails: any
}

export const initialState: RssInitialState = {
  rss: {
    description: '',
    code: '',
    title: '',
    frequency: Frequencies.Weekly,
    dayPart: DayParts.BeforeOpen,
    duration: 0,
    id: 0,
    files: []
  },
  allRssData: [],
  masterTasksSuccess: [],
  restaurantTasksSuccess: [],
  restaurantTasksScheduleSuccess: [],
  restaurantScheduledTasksSuccess: [],
  restaurantScheduledTasksBulkUpdateSuccess: [],
  reportingSuccess: null,
  reportingError: null,
  loading: false,
  errors: null,
  success: null,
  imageUpload: null,
  imageUploadError: null,
  imageUploadSuccess: null,
  formSuccess: null,
  formError: null,
  selectedSubmissionDetails: null
}

interface MasterTasksResponse {
  data: MasterTask[]
}

interface RestaurantTasksResponse {
  data: RestaurantTask[]
}

interface RestaurantTasksScheduleResponse {
  data: RestaurantTaskSchedule[]
}

interface RestaurantScheduledTasksResponse {
  data: RestaurantScheduledTask[]
}

const handleError = (error: any, rejectWithValue: any) => {
  if (error.response) {
    return rejectWithValue(error.response.data)
  }

  return rejectWithValue('An unknown error occurred')
}

export const gygMasterTasks = createAsyncThunk<MasterTasksResponse, void, Redux>(
  RESTO365_RSS_MASTER_TASKS,
  async (_, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-master-tasks`)

      return response.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksById = createAsyncThunk<RestaurantTasksResponse, number, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_BY_RESTAURANT_ID,
  async (formId, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-restaurant-tasks/restaurant/${formId}`)

      return response.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksSubmit = createAsyncThunk<RestaurantTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_CREATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.post('resto365-restaurant-tasks', formData)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksUpdate = createAsyncThunk<RestaurantTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_UPDATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.put(`resto365-restaurant-tasks/${formData.id}`, formData)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksDelete = createAsyncThunk<RestaurantTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_DELETE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.delete(`resto365-restaurant-tasks/${formData.id}`)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksRestore = createAsyncThunk<RestaurantTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_RESTORE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.patch(`resto365-restaurant-tasks/restore/${formData.id}`)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksScheduleSubmit = createAsyncThunk<RestaurantTasksScheduleResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_SCHEDULE_CREATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.post('resto365-restaurant-task-schedule', formData)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTasksScheduleUpdate = createAsyncThunk<RestaurantTasksScheduleResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_SCHEDULE_UPDATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.patch(`resto365-restaurant-task-schedule/${formData.id}`, formData)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantScheduledTasks = createAsyncThunk<RestaurantScheduledTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient(`resto365-restaurant-scheduled-tasks/restaurant/${formData}`)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantScheduledTasksBulkUpdate = createAsyncThunk<RestaurantScheduledTasksResponse, any, Redux>(
  RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS_BULK_UPDATE,
  async (formData, { extra, rejectWithValue }) => {
    try {
      const responseData = await extra.httpClient.patch('resto365-restaurant-scheduled-tasks/bulkUpdate', formData)

      return responseData.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantScheduledTasksBulkUpdateStatus = createAsyncThunk<
  RestaurantScheduledTasksResponse,
  any,
  Redux
>(RESTO365_RSS_RESTAURANT_SCHEDULED_TASKS_BULK_UPDATE_STATUS, async (formData, { extra, rejectWithValue }) => {
  try {
    const responseData = await extra.httpClient.patch(
      'resto365-restaurant-scheduled-tasks/bulkUpdate/updateCriticalStatus',
      formData
    )

    return responseData.data
  } catch (error: any) {
    return handleError(error, rejectWithValue)
  }
})

export const gygRestaurantTaskSubmissionsByRestaurantId = createAsyncThunk<any, number, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_SUBMISSIONS_BY_RESTAURANT_ID,
  async (restaurantId, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-restaurant-task-submissions/restaurant/${restaurantId}`)

      return response.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const gygRestaurantTaskSubmissionsById = createAsyncThunk<any, number, Redux>(
  RESTO365_RSS_RESTAURANT_TASKS_SUBMISSIONS_BY_ID,
  async (restaurantTaskSubmissionId, { extra, rejectWithValue }) => {
    try {
      const response = await extra.httpClient(`resto365-restaurant-task-submissions/${restaurantTaskSubmissionId}`)

      return response.data
    } catch (error: any) {
      return handleError(error, rejectWithValue)
    }
  }
)

export const clearErrorDataRSS = createAsyncThunk<RssInitialState, void, Redux>(
  RESTO365_RSS_INITIAL_STATE,
  async () => {
    return initialState
  }
)

export const resto365Rss = createSlice({
  name: RESTO365_RSS,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder

      .addCase(gygMasterTasks.fulfilled, (state, action) => {
        state.masterTasksSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygMasterTasks.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygMasterTasks.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTasksById.fulfilled, (state, action) => {
        state.restaurantTasksSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantTasksById.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTasksById.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTasksSubmit.fulfilled, (state, action) => {
        state.restaurantTasksSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantTasksSubmit.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTasksSubmit.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTasksUpdate.fulfilled, (state, action) => {
        state.restaurantTasksSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantTasksUpdate.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTasksUpdate.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTasksScheduleSubmit.fulfilled, (state, action) => {
        state.restaurantTasksScheduleSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantTasksScheduleSubmit.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTasksScheduleSubmit.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTasksScheduleUpdate.fulfilled, (state, action) => {
        state.restaurantTasksScheduleSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data Updated successfully'
      })
      .addCase(gygRestaurantTasksScheduleUpdate.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTasksScheduleUpdate.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantScheduledTasks.fulfilled, (state, action) => {
        state.restaurantScheduledTasksSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantScheduledTasks.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantScheduledTasks.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantScheduledTasksBulkUpdate.fulfilled, (state, action) => {
        state.restaurantScheduledTasksBulkUpdateSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantScheduledTasksBulkUpdate.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantScheduledTasksBulkUpdate.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantScheduledTasksBulkUpdateStatus.fulfilled, (state, action) => {
        state.restaurantScheduledTasksBulkUpdateSuccess = action.payload.data
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data submitted successfully'
      })
      .addCase(gygRestaurantScheduledTasksBulkUpdateStatus.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantScheduledTasksBulkUpdateStatus.rejected, state => {
        state.formError = 'Please contact administrator if you are not able to submit form.'
        state.loading = false
      })

      .addCase(gygRestaurantTaskSubmissionsByRestaurantId.fulfilled, (state, action) => {
        state.allRssData = action.payload
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data fetched successfully'
      })
      .addCase(gygRestaurantTaskSubmissionsByRestaurantId.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTaskSubmissionsByRestaurantId.rejected, state => {
        state.formError = 'Failed to fetch data. Please contact the administrator.'
        state.loading = false
      })

      .addCase(gygRestaurantTaskSubmissionsById.fulfilled, (state, action) => {
        state.selectedSubmissionDetails = action.payload
        state.formError = null
        state.loading = false
        state.formSuccess = 'Data fetched successfully'
      })
      .addCase(gygRestaurantTaskSubmissionsById.pending, state => {
        state.errors = null
        state.loading = true
        state.formSuccess = null
      })
      .addCase(gygRestaurantTaskSubmissionsById.rejected, state => {
        state.formError = 'Failed to fetch data. Please contact the administrator.'
        state.loading = false
      })

      .addCase(clearErrorDataRSS.fulfilled, state => {
        state.success = null
        state.errors = null
        state.formError = null
        state.formSuccess = null
        state.reportingError = null
        state.reportingSuccess = null
        state.loading = false
      })
  }
})

export default resto365Rss.reducer
